import React from 'react';
import { Link } from 'react-router-dom';

const CollegeEducation = () => {
  const programs = [
    {
      title: "Associate Degree in Early Childhood Education",
      path: "/academics/ass_early_ed",
      description: "Foundation in early childhood development and teaching",
      icon: "👶"
    },
    {
      title: "Bachelor of Science in Early Childhood Education",
      path: "/academics/bs_early_ed",
      description: "Comprehensive undergraduate program in early childhood education",
      icon: "🎓"
    },
    {
      title: "Master of Science in Early Childhood Education",
      path: "/academics/ms_early_ed",
      description: "Advanced study in early childhood development and pedagogy",
      icon: "📚"
    },
    {
      title: "Master of Science in School Administration and Supervision",
      path: "/academics/ed_admin",
      description: "Leadership training for educational administrators",
      icon: "👥"
    }
  ];

  const features = [
    {
      title: "Research Excellence",
      description: "Cutting-edge research opportunities and innovative teaching methods",
      icon: "🔬"
    },
    {
      title: "Professional Development",
      description: "Practical experience through teaching assistantships and supervised field work",
      icon: "💼"
    },
    {
      title: "Diverse Programs",
      description: "Comprehensive range of degrees from associate to doctoral level",
      icon: "📋"
    },
    {
      title: "Expert Faculty",
      description: "Learn from experienced educators and researchers in the field",
      icon: "👨‍🏫"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Wood College of Education
            </h1>
            <p className="text-xl opacity-90">
              Shaping the future of education through excellence in teaching and research
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">About Our College</h2>
              <div className="prose max-w-none text-gray-600">
                <p className="mb-4">
                  The Wood School of Education has gained an international reputation for the 
                  diversity and excellence of its programs. The School continues its tradition 
                  of placing education first—exemplified through innovative teaching methods, 
                  cutting-edge research, and wide-reaching service.
                </p>
                <p className="mb-4">
                  Our mission is to develop effective professionals and scholars informed by 
                  theory, research, and best practice. We believe in the critical importance 
                  of education in an open, democratic, and diverse society, preparing our 
                  graduates to meet, evaluate, and provide effective leadership in these 
                  challenging times.
                </p>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Why Choose Us</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Programs Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Our Programs</h2>
            <div className="space-y-4">
              {programs.map((program, index) => (
                <Link 
                  key={index}
                  to={program.path}
                  className="block bg-white rounded-xl shadow-sm p-8 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{program.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold group-hover:text-primary-600 transition-colors">
                        {program.title}
                      </h3>
                      <p className="text-gray-600 mt-2">{program.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Research and Opportunities Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Research and Opportunities</h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Graduate Opportunities</h3>
                  <p className="text-gray-600">
                    Graduate students supplement coursework with various paraprofessional 
                    activities, including research assistance, teaching, and supervised field 
                    experiences. Research assistantships, fellowships, and scholarships are 
                    available for qualified candidates.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Research Focus</h3>
                  <p className="text-gray-600">
                    We encourage an interdisciplinary approach to research and scholarship. 
                    Our faculty collaborates with diverse scholars and practitioners both 
                    within and outside the field of education.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Start Your Educational Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of educators and researchers in shaping the future of education.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/application"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                >
                  Apply Now
                </Link>
                <Link
                  to="/request"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-primary-700 bg-white border border-primary-200 hover:bg-primary-50 transition-colors duration-200"
                >
                  Request Information
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CollegeEducation; 